import { Tag as PBTag, Tag_Type } from "gen/tag/models_pb"
import { ReactNode, useMemo } from "react"
import { UUID } from "shared-ts/classes/uuid"
import {
  Tag,
  extractTagColor,
} from "shared-web/components/base/informational/Tag"
import {
  usePopoverSelect,
  UsePopoverSelectProps,
  UsePopoverSelectRes,
} from "shared-web/components/composite/popover"

import { useSearchTags } from "features/api/search"

interface UseTagsPopoverSelectProps
  extends Omit<
    UsePopoverSelectProps<PBTag>,
    "items" | "keyExtractor" | "labelExtractor" | "onChangeQuery"
  > {
  tagType: Tag_Type
}

export type UseTagsPopoverSelectRes = UsePopoverSelectRes<PBTag>

function useTagsPopoverSelect({
  tagType,
  ...rest
}: UseTagsPopoverSelectProps): UseTagsPopoverSelectRes {
  const { results, setQuery } = useSearchTags(
    { types: [tagType] },
    { ignoreSearchQuery: true }
  )

  const tags = useMemo(() => {
    const res: PBTag[] = []
    for (const { tag } of results) {
      if (!tag) continue
      res.push(tag)
    }
    return res
  }, [results])

  return usePopoverSelect({
    items: tags,
    keyExtractor,
    labelExtractor,
    contentExtractor,
    onChangeQuery: setQuery,
    ...rest,
  })
}

function keyExtractor(tag: PBTag): string {
  return UUID.idString(tag.id)
}

function labelExtractor(tag: PBTag): string {
  return tag.name
}

function contentExtractor(tag: PBTag): ReactNode {
  return <Tag name={tag.name} color={extractTagColor(tag.color)} />
}

export default useTagsPopoverSelect
